import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Copy, Search } from "lucide-react";
import { useState } from "react";

const searchSchema = z.object({
  query: z.string().min(1, "Search query is required"),
});

type SearchValues = z.infer<typeof searchSchema>;

interface BlockHeightSearchBarProps {
  onUpdate?: (values: { searchType: "block_height"; query: string }) => void;
  defaultValues?: { query: string };
}

export default function BlockHeightSearchBar({ onUpdate, defaultValues }: BlockHeightSearchBarProps) {
  const form = useForm<SearchValues>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      query: defaultValues?.query || "",
    },
  });
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCopy = () => {
    const value = form.getValues("query");
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const onSubmit = async (data: SearchValues) => {
    setIsSubmitting(true);
    try {
      onUpdate?.({ searchType: "block_height", query: data.query });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4 bg-white dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700 w-full"
    >
      <div className="flex gap-6">
        {/* Search Type Label */}
        <div className="flex flex-col w-32">
          <span className="text-xs text-gray-500 mb-1">Search Type</span>
          <div className="h-9 flex items-center px-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800">
            <span className="text-sm text-gray-700 dark:text-gray-300">Block Height</span>
          </div>
        </div>
        
        {/* Search Query and Update Button */}
        <div className="flex flex-col flex-1 min-w-[260px]">
          <span className="text-xs text-gray-500 mb-1">Search Query</span>
          <div className="flex items-center gap-2">
            <div className="relative flex items-center flex-1">
              <Input
                {...form.register("query")}
                className="pr-10 h-9"
                placeholder="Enter block height (number)"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-500 hover:text-blue-700"
                onClick={handleCopy}
                tabIndex={-1}
              >
                {copied ? <span className="text-xs">Copied!</span> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {/* Update Button */}
            <Button
              type="submit"
              variant="outline"
              className="flex items-center dark:text-white dark:hover:text-white dark:hover:bg-blue-700 dark:border-gray-700 h-9"
              disabled={isSubmitting}
            >
              <Search className="w-4 h-4 mr-1" /> Update
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
} 